import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Share2, PenTool, Square, Circle as CircleIcon, Type, Eraser, Trash2, Undo, Redo, Link as LinkIcon, X, Highlighter, Move } from 'lucide-react';
import LZString from 'lz-string';
import SnapshotToast from '../../components/SnapshotToast';
import ConfirmationModal from '../../components/ConfirmationModal';
import ThoughtBubbleIcon from '../../components/ThoughtBubbleIcon';

const COLORS = [
    { id: 'black', value: '#1f2937' },
    { id: 'red', value: '#ef4444' },
    { id: 'green', value: '#22c55e' },
    { id: 'blue', value: '#3b82f6' },
    { id: 'orange', value: '#f97316' },
    { id: 'purple', value: '#a855f7' },
];

const TOOLS = {
    SELECT: 'select',
    PEN: 'pen',
    RECT: 'rect',
    CIRCLE: 'circle',
    TEXT: 'text',
    ERASER: 'eraser',
};

// Hit Test Helpers
const distanceToSegment = (p, v, w) => {
    const l2 = Math.pow(v.x - w.x, 2) + Math.pow(v.y - w.y, 2);
    if (l2 === 0) return Math.sqrt(Math.pow(p.x - v.x, 2) + Math.pow(p.y - v.y, 2));
    let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
    t = Math.max(0, Math.min(1, t));
    return Math.sqrt(Math.pow(p.x - (v.x + t * (w.x - v.x)), 2) + Math.pow(p.y - (v.y + t * (w.y - v.y)), 2));
};

const getElementAtPosition = (x, y, elements) => {
    for (let i = elements.length - 1; i >= 0; i--) {
        const el = elements[i];
        if (el.type === 'rect') {
            // Normalizing rect coords in case w/h are negative (though drawing logic handles abs)
            const minX = Math.min(el.x, el.x + el.w);
            const maxX = Math.max(el.x, el.x + el.w);
            const minY = Math.min(el.y, el.y + el.h);
            const maxY = Math.max(el.y, el.y + el.h);
            if (x >= minX && x <= maxX && y >= minY && y <= maxY) return el;
        } else if (el.type === 'circle') {
            const dist = Math.sqrt(Math.pow(x - el.cx, 2) + Math.pow(y - el.cy, 2));
            if (dist <= el.r) return el;
        } else if (el.type === 'text') {
            // Approx text box
            const width = el.text.length * (el.fontSize * 0.6);
            const height = el.fontSize;
            // SVG Text y is usually the baseline. We assume hit box goes up from y.
            if (x >= el.x && x <= el.x + width && y >= el.y - height && y <= el.y + height * 0.3) return el;
        } else if (el.type === 'path') {
            for (let j = 0; j < el.points.length - 1; j++) {
                if (distanceToSegment({ x, y }, el.points[j], el.points[j + 1]) < 10) return el;
            }
        }
    }
    return null;
};

// --- Text Modal Component ---
const TextModal = ({ isOpen, onClose, onSubmit }) => {
    const [text, setText] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setText('');
            // Small timeout to ensure render before focus
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isOpen]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (text.trim()) onSubmit(text);
        } else if (e.key === 'Escape') {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-[90%] max-w-md transform transition-all scale-100">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Add Text</h3>
                <input
                    ref={inputRef}
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type something..."
                    className="w-full border-2 border-purple-100 focus:border-purple-500 rounded-lg p-3 text-lg outline-none mb-6 transition-colors text-slate-700 placeholder:text-slate-300"
                />
                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-slate-500 hover:bg-slate-100 rounded-lg font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => text.trim() && onSubmit(text)}
                        className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-bold shadow-lg shadow-purple-200 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!text.trim()}
                    >
                        Add Text
                    </button>
                </div>
            </div>
        </div>
    );
};

const WhiteboardItApp = () => {
    const navigate = useNavigate();

    // State
    const [elements, setElements] = useState([]);
    const [history, setHistory] = useState([]); // Undo History
    const [redoStack, setRedoStack] = useState([]); // Redo Stack
    const [currentTool, setCurrentTool] = useState(TOOLS.PEN);
    const [currentColor, setCurrentColor] = useState(COLORS[0].value);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentElement, setCurrentElement] = useState(null); // The one being drawn
    const [showToast, setShowToast] = useState(false);
    const [cursorState, setCursorState] = useState('default');

    // Selection / Dragging State
    const [selectedElement, setSelectedElement] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [dragStartMouse, setDragStartMouse] = useState(null); // For path drag calculation
    const [dragSnapshot, setDragSnapshot] = useState(null); // Snap of elements before drag

    // Text Modal State
    const [isTextModalOpen, setIsTextModalOpen] = useState(false);
    const [textCoords, setTextCoords] = useState(null);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    const svgRef = useRef(null);
    const [viewBox, setViewBox] = useState({ x: 0, y: 0, w: 0, h: 0 }); // Dynamic resizing? Or just fixed?
    // For simplicity, we'll map pointer coordinates directly to screen 

    // --- Toast Logic ---
    useEffect(() => {
        let timer;
        if (showToast) {
            timer = setTimeout(() => setShowToast(false), 6000);
        }
        return () => clearTimeout(timer);
    }, [showToast]);

    // --- Keyboard Shortcuts (Undo) ---
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                handleUndo();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }); // Intentionally no dep array to always get latest scope? 
    // No, that's bad for perf. But handleUndo isn't stable.
    // I'll allow re-bind on render. Or better, use a ref for history?
    // Let's stick to dependency on history.
    // Wait, if I put handleUndo below, I can't check it here easily.
    // I need to define handleUndo before this effect or use a useCallback.
    // I'll define handleUndo inside the effect or outside.
    // I'll put the Effect AFTER handleUndo definition.

    // --- State Management (URL) ---
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const wbData = params.get('wb');
        if (wbData) {
            try {
                const decompressed = LZString.decompressFromEncodedURIComponent(wbData);
                if (decompressed) {
                    setElements(JSON.parse(decompressed));
                }
            } catch (e) {
                console.error("Failed to load whiteboard", e);
            }
        }
    }, []);

    const saveToUrl = (newElements) => {
        const json = JSON.stringify(newElements);
        const compressed = LZString.compressToEncodedURIComponent(json);
        const url = new URL(window.location);
        url.searchParams.set('wb', compressed);
        window.history.replaceState(null, '', url);
    };

    const updateElements = (newElements, saveHistory = true) => {
        if (saveHistory) {
            setHistory(prev => [...prev.slice(-49), elements]);
            setRedoStack([]); // Clear redo stack on new action
        }
        setElements(newElements);
        saveToUrl(newElements);
    };

    const handleUndo = () => {
        if (history.length === 0) return;
        const previousState = history[history.length - 1];
        const newHistory = history.slice(0, -1);

        setRedoStack(prev => [...prev, elements]); // Push current to redo
        setHistory(newHistory);
        setElements(previousState);
        saveToUrl(previousState);
    };

    const handleRedo = () => {
        if (redoStack.length === 0) return;
        const nextState = redoStack[redoStack.length - 1]; // Last item pushed is the immediate future
        const newRedoStack = redoStack.slice(0, -1);

        setHistory(prev => [...prev, elements]); // Push current to history
        setRedoStack(newRedoStack);
        setElements(nextState);
        saveToUrl(nextState);
    };

    // Move Effect here to access handleUndo
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                if (e.shiftKey) {
                    handleRedo();
                } else {
                    handleUndo();
                }
            } else if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
                e.preventDefault();
                handleRedo();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [history, redoStack, elements]); // Re-bind when state changes

    // --- Drawing Logic ---

    const getCoords = (e) => {
        const rect = svgRef.current.getBoundingClientRect();
        return {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        };
    };

    const handlePointerDown = (e) => {
        const { x, y } = getCoords(e);

        if (currentTool === TOOLS.SELECT) {
            const hit = getElementAtPosition(x, y, elements);
            if (hit) {
                setSelectedElement(hit);
                setIsDragging(true);
                setDragSnapshot(elements); // Save state before drag for history

                // Calculate Drag Offsets
                if (hit.type === 'rect') {
                    setDragOffset({ x: x - hit.x, y: y - hit.y });
                } else if (hit.type === 'circle') {
                    setDragOffset({ x: x - hit.cx, y: y - hit.cy });
                } else if (hit.type === 'text') {
                    setDragOffset({ x: x - hit.x, y: y - hit.y });
                } else if (hit.type === 'path') {
                    setDragStartMouse({ x, y });
                }
            } else {
                setSelectedElement(null); // Deselect if clicked empty space
            }
            return;
        }

        if (currentTool === TOOLS.ERASER) return;

        setIsDrawing(true);

        if (currentTool === TOOLS.PEN) {
            setCurrentElement({
                id: Date.now(),
                type: 'path',
                points: [{ x, y }],
                color: currentColor,
                strokeWidth: 3
            });
        } else if (currentTool === TOOLS.RECT) {
            setCurrentElement({
                id: Date.now(),
                type: 'rect',
                startX: x, startY: y,
                color: currentColor,
                strokeWidth: 3,
                x, y, w: 0, h: 0
            });
        } else if (currentTool === TOOLS.CIRCLE) {
            setCurrentElement({
                id: Date.now(),
                type: 'circle',
                startX: x, startY: y,
                color: currentColor,
                strokeWidth: 3,
                cx: x, cy: y, r: 0
            });
        } else if (currentTool === TOOLS.TEXT) {
            setTextCoords({ x, y });
            setIsTextModalOpen(true);
            setIsDrawing(false);
        }
    };

    const handleTextSubmit = (text) => {
        if (!textCoords) return;

        const newEl = {
            id: Date.now(),
            type: 'text',
            x: textCoords.x,
            y: textCoords.y,
            color: currentColor,
            text,
            fontSize: 20
        };
        updateElements([...elements, newEl]);
        setIsTextModalOpen(false);
        setTextCoords(null);
    };

    const handlePointerMove = (e) => {
        const { x, y } = getCoords(e);

        // --- Select / Move Logic ---
        if (currentTool === TOOLS.SELECT) {
            if (isDragging && selectedElement) {
                setCursorState('grabbing');

                const updatedElements = elements.map(el => {
                    if (el.id === selectedElement.id) {
                        if (el.type === 'rect') {
                            return { ...el, x: x - dragOffset.x, y: y - dragOffset.y };
                        } else if (el.type === 'circle') {
                            return { ...el, cx: x - dragOffset.x, cy: y - dragOffset.y };
                        } else if (el.type === 'text') {
                            return { ...el, x: x - dragOffset.x, y: y - dragOffset.y };
                        } else if (el.type === 'path') {
                            const dx = x - dragStartMouse.x;
                            const dy = y - dragStartMouse.y;
                            // For path, we need to restart the 'dragStartMouse' every frame if we want delta,
                            // OR map from original points. 
                            // Easier: Calculate delta from LAST move? No, accumulation error.
                            // Better: We are not storing the original points, so we can't do (original + delta).
                            // We are modifying 'elements' state in place.
                            // So we must move by (current - prevMouse).
                            // But handlePointerMove fires rapidly.
                            // Let's use `dragStartMouse` as "Previous Mouse Position" for paths.

                            const newPoints = el.points.map(p => ({ x: p.x + dx, y: p.y + dy }));
                            return { ...el, points: newPoints };
                        }
                    }
                    return el;
                });

                if (selectedElement.type === 'path') {
                    setDragStartMouse({ x, y }); // Update ref for next delta
                }

                setElements(updatedElements);
                // Keep selectedElement reference in sync for rendering box
                setSelectedElement(prev => updatedElements.find(u => u.id === prev.id));

            } else {
                // Hover Logic
                const hit = getElementAtPosition(x, y, elements);
                setCursorState(hit ? 'move' : 'default');
            }
            return;
        }

        if (!isDrawing || !currentElement) return;

        if (currentElement.type === 'path') {
            setCurrentElement(prev => ({
                ...prev,
                points: [...prev.points, { x, y }]
            }));
        } else if (currentElement.type === 'rect') {
            setCurrentElement(prev => ({
                ...prev,
                x: Math.min(prev.startX, x),
                y: Math.min(prev.startY, y),
                w: Math.abs(x - prev.startX),
                h: Math.abs(y - prev.startY)
            }));
        } else if (currentElement.type === 'circle') {
            const r = Math.sqrt(Math.pow(x - currentElement.startX, 2) + Math.pow(y - currentElement.startY, 2));
            setCurrentElement(prev => ({
                ...prev,
                r
            }));
        }
    };

    const handlePointerUp = () => {
        if (currentTool === TOOLS.SELECT) {
            if (isDragging) {
                // Commit to History
                if (dragSnapshot) {
                    setHistory(prev => [...prev.slice(-49), dragSnapshot]);
                    setRedoStack([]);
                    saveToUrl(elements);
                }
                setIsDragging(false);
                setDragSnapshot(null);
                setCursorState('default'); // Or stay 'move' if still hovering? 'default' is safer reset.
            }
            return;
        }

        if (!isDrawing) return;
        setIsDrawing(false);
        if (currentElement) {
            updateElements([...elements, currentElement]);
            setCurrentElement(null);
        }
    };

    const handleEraserClick = (id) => {
        if (currentTool === TOOLS.ERASER) {
            updateElements(elements.filter(el => el.id !== id));
        }
    };

    const handleClear = () => {
        setIsClearModalOpen(true);
    };

    const confirmClearBoard = () => {
        updateElements([]);
        setIsClearModalOpen(false);
    };

    const handleShare = () => {
        saveToUrl(elements);
        navigator.clipboard.writeText(window.location.href);
        setShowToast(true);
    };

    // --- Renderers ---    
    // --- Renderers ---    
    const renderElement = (el) => {
        const isSelected = selectedElement && selectedElement.id === el.id;
        const commonProps = {
            stroke: el.color,
            fill: 'none',
            strokeWidth: el.strokeWidth || 3,
            strokeLinecap: 'round',
            strokeLinejoin: 'round',
            // Interactive props for Eraser
            className: currentTool === TOOLS.ERASER ? 'cursor-pointer hover:opacity-50' : '',
            onClick: () => handleEraserClick(el.id),
            pointerEvents: currentTool === TOOLS.ERASER ? 'all' : 'none'
        };

        let renderedContent;

        if (el.type === 'path') {
            const d = el.points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
            renderedContent = <path key={el.id} d={d} {...commonProps} />;
        } else if (el.type === 'rect') {
            renderedContent = <rect key={el.id} x={el.x} y={el.y} width={el.w} height={el.h} {...commonProps} />;
        } else if (el.type === 'circle') {
            renderedContent = <circle key={el.id} cx={el.cx} cy={el.cy} r={el.r} {...commonProps} />;
        } else if (el.type === 'text') {
            renderedContent = (
                <text
                    key={el.id}
                    x={el.x}
                    y={el.y}
                    fill={el.color}
                    fontSize={el.fontSize}
                    fontFamily="Nunito, sans-serif"
                    fontWeight="bold"
                    className={currentTool === TOOLS.ERASER ? 'cursor-pointer hover:opacity-50 select-none' : 'select-none'}
                    onClick={() => handleEraserClick(el.id)}
                    style={{ pointerEvents: currentTool === TOOLS.ERASER ? 'all' : 'none' }}
                >
                    {el.text}
                </text>
            );
        }

        // Selection Overlay
        if (isSelected) {
            // Need bounding box logic for selection ring
            // For Text/Path, we only have approx or exact points.
            // Simplified: Just render the content again with blue stroke? 
            // Better: Render a bounding box if possible.
            // For simplicity in SVG (without calculating BBox of Path): 
            // We can just render a blue outline.
            // Actually, the user asked for a "Blue Bounding Box".
            // Rect/Circle is easy. Path/Text is hard without `getBBox`.
            // Approximation for Path: Min/Max X/Y.

            let bbox = { x: 0, y: 0, w: 0, h: 0 };
            if (el.type === 'rect') {
                bbox = { x: el.x, y: el.y, w: el.w, h: el.h };
            } else if (el.type === 'circle') {
                bbox = { x: el.cx - el.r, y: el.cy - el.r, w: el.r * 2, h: el.r * 2 };
            } else if (el.type === 'text') {
                bbox = { x: el.x, y: el.y - el.fontSize, w: el.text.length * (el.fontSize * 0.6), h: el.fontSize * 1.5 };
            } else if (el.type === 'path') {
                const xs = el.points.map(p => p.x);
                const ys = el.points.map(p => p.y);
                const minX = Math.min(...xs);
                const maxX = Math.max(...xs);
                const minY = Math.min(...ys);
                const maxY = Math.max(...ys);
                bbox = { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
            }

            return (
                <g key={el.id + '-group'}>
                    {renderedContent}
                    <rect
                        x={bbox.x - 5} y={bbox.y - 5}
                        width={bbox.w + 10} height={bbox.h + 10}
                        fill="none" stroke="#3b82f6" strokeWidth="1" strokeDasharray="5,3"
                        pointerEvents="none"
                    />
                </g>
            );
        }

        return renderedContent;
    };

    return (
        <div className="h-screen fixed inset-0 bg-white flex flex-col font-sans overflow-hidden touch-none selection:none">
            {/* Header */}
            <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-50">
                <div
                    className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity w-1/3"
                    onClick={() => navigate('/')}
                >
                    <ThoughtBubbleIcon className="fill-blue-600 text-blue-600 shrink-0" size={24} />
                    <span className="text-sm md:text-xl font-bold md:font-extrabold text-blue-600 tracking-tight font-rounded">
                        Thoughtblink
                    </span>
                </div>

                <div className="flex justify-center w-1/3">
                    <h1 className="text-lg md:text-xl font-bold text-purple-600">
                        Whiteboard-It
                    </h1>
                </div>

                <div className="flex items-center justify-end w-1/3 gap-4">
                    <button
                        onClick={handleShare}
                        className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-full transition-all"
                        title="Share Board"
                    >
                        <Share2 size={20} />
                    </button>
                    <button
                        onClick={handleClear}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
                        title="Clear Board"
                    >
                        <Trash2 size={20} />
                    </button>
                </div>
            </header>

            {/* Canvas Area */}
            <main className="flex-grow p-4 md:p-8 flex flex-col overflow-hidden bg-slate-200">
                <div className="flex-grow relative rounded-lg shadow-2xl overflow-hidden bg-gradient-to-br from-gray-300 via-gray-100 to-gray-400 p-3 md:p-4">
                    {/* The Inner Whiteboard Surface */}
                    <div className="w-full h-full bg-white shadow-inner rounded-sm relative cursor-crosshair">
                        <svg
                            ref={svgRef}
                            className={`w-full h-full block touch-none ${cursorState}`}
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerUp}
                            // Prevent scrolling on touch
                            style={{ touchAction: 'none', cursor: cursorState === 'default' ? 'crosshair' : cursorState }}
                        // If select mode, default is default, hit is move, dragging is grabbing.
                        // If draw mode, cursor is crosshair.
                        >
                            {elements.map(renderElement)}
                            {currentElement && renderElement(currentElement)}
                        </svg>
                    </div>
                </div>
            </main>

            {/* Floating Toolbar */}
            <div className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-white rounded-2xl md:rounded-full shadow-xl border border-gray-200 p-3 md:px-4 flex flex-wrap md:flex-nowrap justify-center items-center gap-1 md:gap-2 gap-y-2 z-50 w-[96%] md:w-auto">

                {/* Undo */}
                <button
                    onClick={handleUndo}
                    disabled={history.length === 0}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl transition-all text-slate-400 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent"
                    title="Undo (Cmd+Z)"
                >
                    <Undo size={20} />
                </button>

                {/* Redo */}
                <button
                    onClick={handleRedo}
                    disabled={redoStack.length === 0}
                    className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl transition-all text-slate-400 hover:bg-slate-50 hover:text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent"
                    title="Redo (Cmd+Y)"
                >
                    <Redo size={20} />
                </button>

                <div className="w-px h-8 bg-gray-200 mx-1 hidden md:block"></div>

                {/* Tools */}
                {[
                    { id: TOOLS.SELECT, icon: Move, label: "Select & Move" },
                    { id: TOOLS.PEN, icon: Highlighter, label: "Pen" },
                    { id: TOOLS.RECT, icon: Square, label: "Rectangle" },
                    { id: TOOLS.CIRCLE, icon: CircleIcon, label: "Circle" },
                    { id: TOOLS.TEXT, icon: Type, label: "Text" },
                    { id: TOOLS.ERASER, icon: Eraser, label: "Eraser" },
                ].map((tool) => (
                    <button
                        key={tool.id}
                        onClick={() => setCurrentTool(tool.id)}
                        className={`w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl transition-all ${currentTool === tool.id
                            ? 'bg-purple-100 text-purple-600'
                            : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                            }`}
                        title={tool.label}
                    >
                        <tool.icon size={20} />
                    </button>
                ))}

                <div className="w-px h-8 bg-gray-200 mx-1 hidden md:block"></div>

                {/* Colors */}
                <div className="flex items-center gap-2 px-1">
                    {COLORS.map(c => (
                        <button
                            key={c.id}
                            onClick={() => setCurrentColor(c.value)}
                            className={`w-8 h-8 flex-shrink-0 rounded-full border-2 transition-transform ${currentColor === c.value ? 'border-cyan-500 scale-110' : 'border-transparent hover:scale-105'
                                }`}
                            style={{ backgroundColor: c.value }}
                            title={c.id}
                        />
                    ))}
                </div>

                <div className="w-px h-8 bg-gray-200 mx-1 hidden md:block"></div>

                {/* Actions */}
                {/* Actions - Trash Removed from here */}

            </div>

            <SnapshotToast isVisible={showToast} onClose={() => setShowToast(false)} color="purple" />
            <TextModal isOpen={isTextModalOpen} onClose={() => setIsTextModalOpen(false)} onSubmit={handleTextSubmit} />
            <ConfirmationModal
                isOpen={isClearModalOpen}
                onClose={() => setIsClearModalOpen(false)}
                onConfirm={confirmClearBoard}
                title="Clear Board?"
                message="Are you sure you want to delete all diagrams? This action cannot be undone."
                confirmText="Yes, Clear All"
            />
        </div>
    );
};

export default WhiteboardItApp;
